import React from 'react';
import { Link } from 'react-router-dom';
import { usePageLastUpdated } from '../hooks/usePageLastUpdated';
import './BioPage.css';

// Optional: set to override fallback (page JS file timestamp)
const PAGE_LAST_UPDATED = undefined; // e.g. { dateTime: '2025-12-23', displayDate: 'December 23, 2025' }

const BioPage = () => {
  usePageLastUpdated(PAGE_LAST_UPDATED);
  return (
    <div className="bio-page">
      <h1>Vita</h1>
      <p>
        <Link to="/">I</Link> am a product manager at{' '}
        <a
          href="https://grafana.com/"
          title="Go to the Grafana Labs web site."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Grafana Labs
        </a>
        , working on Grafana's AI products for observability. My scope includes Grafana's AI
        assistant, agentic SRE workflows, and applied ML for forecasting and anomaly detection. I
        also led product strategy for distributed tracing across open source{' '}
        <a
          href="https://grafana.com/oss/tempo/"
          title="Go to the Grafana Tempo web site."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Grafana Tempo
        </a>
        ,{' '}
        <a
          href="https://grafana.com/products/cloud/"
          title="Go to the Grafana Cloud web site."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Grafana Cloud Traces
        </a>
        , and Grafana Enterprise Traces. My work sits at the intersection of product strategy,
        platform engineering, and customer impact. I partner closely with engineering, design, GTM,
        and enterprise customers to translate complex technical capabilities into products with clear
        adoption, usage, and revenue outcomes.
      </p>
      <p>
        Before Grafana Labs, I held multiple senior product and engineering roles at{' '}
        <a
          href="https://www.adevinta.com/"
          title="Go to the Adevinta web site."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Adevinta
        </a>
        , where I led the product vision for a large-scale internal developer platform on AWS. I was
        responsible for cloud, developer, and security platforms spanning DevOps, SecOps, and
        FinOps, with a strong focus on developer experience, delivery performance, and platform
        efficiency.
      </p>
      <p>
        Earlier in my career, I was a senior software engineer and technical lead at{' '}
        <a
          href="https://www.midokura.com/"
          title="Go to the Midokura web site."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Midokura
        </a>
        , contributing to network virtualization software for{' '}
        <a
          href="https://www.openstack.org/"
          title="Go to the OpenStack web site."
          target="_blank"
          rel="noopener"
          className="external"
        >
          OpenStack
        </a>
        {' '}and leading SaaS analytics products. I began my industry career at{' '}
        <a
          href="https://www.alcatel-lucent.com/"
          title="Go to the Alcatel-Lucent web site."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Alcatel-Lucent
        </a>
        , working on large-scale billing and network management systems for telecom operators.
      </p>
      <p>
        Before returning to industry, I worked in academia as a postdoctoral researcher and lecturer
        at{' '}
        <a
          href="https://www.upf.edu/"
          title="Go to the web site of the Pompeu Fabra University."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Pompeu Fabra University
        </a>
        , and as a doctoral researcher at{' '}
        <a
          href="https://www.uc3m.es/"
          title="Go to the web site of the Carlos III University."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Carlos III University of Madrid
        </a>{' '}
        and the{' '}
        <a
          href="https://www.networks.imdea.org/"
          title="Go to the IMDEA Networks Institute web site."
          target="_blank"
          rel="noopener noreferrer"
          className="external"
        >
          IMDEA Networks Institute
        </a>
        . My research focused on distributed systems, networking, and large-scale measurement, with
        peer-reviewed publications and teaching experience in telecommunications and computer
        networks.
      </p>
      <p>
        I hold a{' '}
        <Link to="/research/phd" title="Go the web page of my doctoral dissertation.">
          PhD
        </Link>{' '}
        and MSc in Telecommunications Engineering from{' '}
        <a
          href="https://www.uc3m.es/"
          title="Go to the web site of the Carlos III University."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Carlos III University of Madrid
        </a>{' '}
        and graduated top of my class in Telecommunications Engineering from the{' '}
        <a
          href="https://www.etti.utcluj.ro/"
          title="Go to the Faculty of Electronics, Telecommunications and Information Technology web site."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Faculty of Electronics, Telecommunications and Information Technology
        </a>{' '}
        at the{' '}
        <a
          href="https://www.utcluj.ro/"
          title="Go to the Technical University of Cluj-Napoca web site."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Technical University of Cluj-Napoca
        </a>
        .
      </p>
      <p>
        My background spans product leadership, platform engineering, and research. I specialize in
        building technically deep products, aligning stakeholders, and scaling platforms used by
        thousands of engineers and enterprises worldwide.
      </p>
      <h1>About Me</h1>
      <h2>Professional Info</h2>
      <p>
        <a href="/download/cv-en.pdf" target="_blank" rel="noopener">
          Download my full resume
        </a>{' '}
        (PDF/180.9 KB) | <Link to="/research">Check my research</Link> |{' '}
        <Link to="/publications">View my publications</Link> |{' '}
        <Link to="/projects">View my projects</Link>
      </p>
      <h2>Companies and Institutions I've Worked For</h2>
      <p>
        <a href="https://grafana.com/" target="_blank" rel="noopener" className="external">
          Grafana Labs
        </a>{' '}
        |{' '}
        <a href="https://www.adevinta.com/" target="_blank" rel="noopener" className="external">
          Adevinta
        </a>{' '}
        |{' '}
        <a
          href="https://www.schibsted.com/"
          target="_blank"
          rel="noopener"
          className="external"
        >
          Schibsted Media Group
        </a>{' '}
        |{' '}
        <a href="https://www.midokura.com/" target="_blank" rel="noopener" className="external">
          Midokura
        </a>{' '}
        |{' '}
        <a href="https://www.upf.edu/" target="_blank" rel="noopener" className="external">
          Pompeu Fabra University
        </a>{' '}
        |{' '}
        <a
          href="https://www.networks.imdea.org/"
          target="_blank"
          rel="noopener noreferrer"
          className="external"
        >
          Institute IMDEA Networks
        </a>{' '}
        |{' '}
        <a
          href="https://www.alcatel-lucent.com/"
          target="_blank"
          rel="noopener"
          className="external"
        >
          Alcatel-Lucent
        </a>{' '}
        |{' '}
        <a href="https://www.siemens.com/" target="_blank" rel="noopener" className="external">
          Siemens
        </a>
      </p>
      <h2>Schools I've Attended</h2>
      <p>
        <a href="https://www.uc3m.es/" target="_blank" rel="noopener" className="external">
          Carlos III University of Madrid
        </a>{' '}
        |{' '}
        <a href="https://www.upc.edu/" target="_blank" rel="noopener" className="external">
          Polytechnic University of Catalonia
        </a>{' '}
        |{' '}
        <a href="https://www.utcluj.ro/" target="_blank" rel="noopener" className="external">
          Technical University of Cluj-Napoca
        </a>{' '}
        |{' '}
        <a
          href="http://www.cni-hd.ro/"
          target="_blank"
          rel="noopener"
          className="external"
        >
          &quot;Traian Lalescu&quot; Computer Science High-School
        </a>{' '}
        | School No. 8 Hunedoara
      </p>
      <h2>Where I grew up</h2>
      <p>
        <a
          href="https://en.wikipedia.org/wiki/Hunedoara"
          title="More on Hunedoara in Wikipedia."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Hunedoara
        </a>
        ,{' '}
        <a
          href="http://en.wikipedia.org/wiki/Transylvania"
          title="More on Transylvania in Wikipedia."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Transylvania
        </a>
        ,{' '}
        <a
          href="http://en.wikipedia.org/wiki/Romania"
          title="More on Romania in Wikipedia."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Romania
        </a>
      </p>
      <h2>Places Where I've Lived</h2>
      <p>
        <a
          href="https://en.wikipedia.org/wiki/Madrid"
          title="More on Madrid in Wikipedia."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Madrid
        </a>{' '}
        |{' '}
        <a
          href="http://en.wikipedia.org/wiki/Sydney"
          title="More on Sydney in Wikipedia."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Sydney
        </a>{' '}
        |{' '}
        <a
          href="http://en.wikipedia.org/wiki/Timişoara"
          title="More on Timișoara in Wikipedia."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Timișoara
        </a>{' '}
        |{' '}
        <a
          href="http://en.wikipedia.org/wiki/San_Francisco"
          title="More on San Francisco in Wikipedia."
          target="_blank"
          rel="noopener"
          className="external"
        >
          San Francisco
        </a>{' '}
        |{' '}
        <a
          href="http://en.wikipedia.org/wiki/Barcelona"
          title="More on Barcelona in Wikipedia."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Barcelona
        </a>{' '}
        |{' '}
        <a
          href="http://en.wikipedia.org/wiki/Cluj-Napoca"
          title="More on Cluj-Napoca in Wikipedia."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Cluj-Napoca
        </a>{' '}
        |{' '}
        <a
          href="http://en.wikipedia.org/wiki/Hunedoara"
          title="More on Hunedoara in Wikipedia."
          target="_blank"
          rel="noopener"
          className="external"
        >
          Hunedoara
        </a>
      </p>
      <h2>Things I Like</h2>
      <p>
        <a
          href="http://photos.bikfalvi.com/"
          target="_blank"
          rel="noopener"
          className="external"
        >
          Photography
        </a>{' '}
        |{' '}
        <a
          href="http://www.anilloverdeciclista.es/"
          target="_blank"
          rel="noopener"
          className="external"
        >
          Cycling
        </a>{' '}
        |{' '}
        <a
          href="https://www.flickr.com/photos/alexbikfalvi/sets/72157607278061706/"
          target="_blank"
          rel="noopener"
          className="external"
        >
          Live music
        </a>{' '}
        | <Link to="/projects">Coding</Link> |{' '}
        <a
          href="https://www.flickr.com/photos/alexbikfalvi/sets/72157619789724242/"
          target="_blank"
          rel="noopener"
          className="external"
        >
          Hiking
        </a>
      </p>
      <h2>Things I Do</h2>
      <p>
        I run, cycle and swim | <Link to="/bio/sport">See my results</Link>
      </p>
      <p>
        Played tamborim, agogô and repinique in the batucada ensemble{' '}
        <a
          href="https://www.facebook.com/pages/Sambanés/106374929450772"
          target="_blank"
          rel="noopener"
          className="external"
        >
          Sambanés
        </a>
        . |{' '}
        <a
          href="http://photos.bikfalvi.com/2012/October/26-28/"
          target="_blank"
          rel="noopener"
          className="external"
        >
          Check us out in Morocco
        </a>
      </p>
    </div>
  );
};

export default BioPage;

